import React, { useCallback, useEffect, useRef, useState } from "react";
import "./index.less";
import {Slider,Select, Spin, InputNumber, Col, Row, InputNumberProps, AutoComplete} from "antd";
// self define
import WeevilTrakStage from "../WeevilTrakButton/WeevilTrakStage";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/redux/reducer/store";
import {
    updateIsClickedSearchButton,
    updateIsGetCurrentLocation,
    updateLocationNameOrCoordinate,
    updateNeedClearLocation,
    updateSelectedLocationItem } from "@/redux/actions/weevilTrakAction";
import {searchLocations} from "@/util/http/WeevilTrak/index";
import {IWeeviltrakSearchLocationsResModel} from "@/util/http/WeevilTrak/responseType";
import {debounce} from "lodash";
import { updateCommonWarnMessage, updateRadiusValue } from "@/redux/actions/weevilTrakAction";
import WeevilTrakInvokeButton from "../WeevilTrakButton/WeevilTrakInvokeButton";
import { allNotBelongUSAStagesName, blockForOneSecond, notSpecialCAStagesName } from "../../weevilTrakCommonFunc";
import { use } from "echarts";

function useTrackedState<T>(initialValue: T) {
    const [state, setState] = useState(initialValue);

    const trackedSetState = useCallback((newValue: T | ((prev: T) => T)) => {
    // 打印当前堆栈
        console.group("WeevilTrakLocation aa State Change Detected");
        console.trace("WeevilTrakLocation aa Stack Trace:");
        console.log("WeevilTrakLocation aa New Value/Updater:", newValue);
        console.groupEnd();

        setState(newValue);
    }, []);

    return [state, trackedSetState] as const;
}

const LightningLocation = () => {
    const dispatch = useDispatch();
    const {
        locationNameOrCoordinate,
        selectedLocationItem,
        radiusValue,
        needClearLocation,
        areasData
    } = useSelector((state: RootState) => state.weevTrakReducer);

    // State Management
    const [inputValue, setInputValue] = useTrackedState(locationNameOrCoordinate); // 替换后
    const [locationData, setLocationData] = useState<IWeeviltrakSearchLocationsResModel[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [selectKey, setSelectKey] = useState(0);
    const selectRef = useRef<any>(null);
    const ignoreNextEmptySearchRef = useRef(false);

    // Helpers
    // Check if string matches strictly "number, number" format
    const isCoordinateFormat = (str: string) => {
        if (!str) return false;
        // Regex allows: optional minus, digits, optional decimal part.
        // Comma separated with optional spaces.
        return /^-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/.test(str.trim());
    };

    // Validates coordinate values and dispatches if valid
    const validateAndDispatchCoordinate = (input: string): boolean => {
        const parts = input.split(",");
        const lat = parseFloat(parts[0].trim());
        const lng = parseFloat(parts[1].trim());

        if (isNaN(lat) || isNaN(lng)) return false;

        if (lat < -90 || lat > 90) {
            setErrorMessage("Latitude must be between -90 and 90");
            return false;
        }
        if (lng < -180 || lng > 180) {
            setErrorMessage("Longitude must be between -180 and 180");
            return false;
        }

        setErrorMessage(null);
        dispatch(updateLocationNameOrCoordinate(input));
        return true;
    };

    // Debounced Search and Validation
    const debouncedFetchOrValidate = React.useCallback(
        debounce(async (searchText: string) => {
            if (!searchText || !searchText.trim()) {
                setIsOpen(false);
                return;
            }

            // 1. If it looks like a coordinate, Validate range, do NOT search
            if (isCoordinateFormat(searchText)) {
                validateAndDispatchCoordinate(searchText);
                setIsOpen(false);
                return;
            }

            // 2. If it implies incomplete coordinate or just number -> Error, no search
            // If user enters purely numeric string that isn't a coord pair yet.
            if (/^-?\d+(\.\d+)?$/.test(searchText.trim())) {
                setErrorMessage("Please enter a location or coordinates");
                setIsOpen(false);
                return;
            }

            // 3. Otherwise treat as Location Name Search
            setLoading(true);
            setIsOpen(true);
            setErrorMessage(null);

            try {
                const res = await searchLocations(searchText);
                setLocationData(res.data || []);
            } catch (error) {
                console.error("Error fetching locations:", error);
                setLocationData([]);
                setErrorMessage("Failed to fetch location data");
            } finally {
                setLoading(false);
            }
        }, 1500), // 1.5 seconds for normal search
        []
    );

    // New Debounced Search for Coordinates (2 seconds)
    const debouncedCoordinateSearch = React.useCallback(
        debounce(async (searchText: string) => {
            if (!searchText || !searchText.trim()) {
                setIsOpen(false);
                return;
            }

            // Validate and dispatch coordinate (update map/state)
            // We check format again just in case, though caller checks too.
            if (isCoordinateFormat(searchText)) {

                // If valid coordinate, proceed with search as well
                setLoading(true);
                setIsOpen(true);
                setErrorMessage(null);

                // Update Redux state with coordinate to trigger map update, etc.
                validateAndDispatchCoordinate(searchText);
                const tmpCoordinates = searchText.split(",").map(coord => parseFloat(coord.trim()));
                // input is in "lat,lng" format, but API expects "lng,lat", so we swap them when creating locationName
                const coordinateStr = `${tmpCoordinates[1]},${tmpCoordinates[0]}`;

                try {
                    const res = await searchLocations(coordinateStr);
                    if(res?.data?.length > 0){
                        const item = res.data?.[0];
                        // check weather belong to the areas covered by the model
                        let isCovered = true;
                        if(item?.details?.country !== "United States" && item?.details?.country !== "Canada"){
                            isCovered = false;
                        }
                        const isBlong = allNotBelongUSAStagesName.some(stage => stage === item?.details?.state || stage === item?.details?.city);
                        const isBlongToCa = notSpecialCAStagesName.some(stage => stage === item?.details?.state || stage === item?.details?.city);

                        if(!isBlong && !isBlongToCa){
                            isCovered = true;
                        }
                        else{
                            isCovered = false;
                        }
                        if(!isCovered){
                            dispatch(updateCommonWarnMessage("The selected location is outside the regions currently supported by WeevilTrak."));
                        }
                    }
                    // setLocationData(res.data || []);
                } catch (error) {
                    console.error("Error fetching locations for coordinate:", error);
                    setLocationData([]);
                    // Optional: setErrorMessage("Failed to fetch location data");
                } finally {
                    setLoading(false);
                }
            }
        }, 2000), // 2 seconds for coordinate search
        []
    );


    const handleSelectSearch = (value: string) => {
        // antd Select (showSearch) may trigger onSearch("") right after blur
        // because it clears its internal search text. Ignore that one-time empty
        // callback so it doesn't overwrite the controlled value.
        if (!value && ignoreNextEmptySearchRef.current) {
            ignoreNextEmptySearchRef.current = false;
            return;
        }

        // setSearchQuery(value);
        setInputValue(value);

        // Check if input looks like coordinates (starts with number/minus, no letters)
        // If so, skip the API search to prevent errors.
        const isInputtingCoordinates = /^[-0-9]/.test(value) && !/[a-zA-Z]/.test(value);

        if (isInputtingCoordinates) {
            // If it strictly matches coordinate format, validate and update immediately
            if (isCoordinateFormat(value)) {
                // Now using debounced search for coordinates
                debouncedCoordinateSearch(value);
            }
            return;
        }

        debouncedFetchOrValidate(value);
    };

    const cleanDoubleComma = (str: string) => {
        // 1. 按逗号拆分成数组
        const parts = str.split(",");

        // 2. 过滤掉空字符串（这是由于连续逗号或逗号+空格产生的）
        const filtered = parts.filter(part => part.trim() !== "");

        // 3. 用单个逗号重新拼接
        return filtered.join(",");
    };
    const handleSelectChange = (value: string) => {
        const item = locationData?.find(info => info.address === value);
        const tmpValue = cleanDoubleComma(value);
        setInputValue(tmpValue);
        // setSearchQuery(value);
        setIsOpen(false);
        setErrorMessage(null);

        dispatch(updateSelectedLocationItem(item));
        dispatch(updateLocationNameOrCoordinate(value));

        // check weather belong to the areas covered by the model
        let isCovered = true;
        if(item?.details?.country !== "United States" && item?.details?.country !== "Canada"){
            isCovered = false;
        }
        const isBlong = allNotBelongUSAStagesName.some(stage => stage === item?.details?.state || stage === item?.details?.city);
        const isBlongToCa = notSpecialCAStagesName.some(stage => stage === item?.details?.state || stage === item?.details?.city);

        if(!isBlong && !isBlongToCa){
            isCovered = true;
        }
        else{
            isCovered = false;
        }
        if(!isCovered){
            dispatch(updateCommonWarnMessage("The selected location is outside the regions currently supported by WeevilTrak."));
        }

        // Blur to close keyboard/focus on mobile
        setTimeout(() => {
            selectRef.current?.blur();
        }, 0);
    };

    const handleBlur = () => {
        // Mark so the subsequent onSearch("") (from antd clearing searchValue)
        // doesn't clear the user's just-typed text.
        ignoreNextEmptySearchRef.current = true;
        setTimeout(() => {
            ignoreNextEmptySearchRef.current = false;
        }, 0);

        if (inputValue) {
            // Only validate as coordinate if it Strictly Looks Like One (regex).
            // This fixes the issue where "City, Country" triggered coordinate validation.
            if (isCoordinateFormat(inputValue)) {
                validateAndDispatchCoordinate(inputValue);
            }
        } else {
            setErrorMessage("Please enter a location or coordinates");
        }
        setIsOpen(false);
        blockForOneSecond();
    };

    const handleFocus = () => {
        // Re-open if valid search text & results exist
        if (inputValue && !isCoordinateFormat(inputValue) && locationData.length > 0) {
            setIsOpen(true);
        }
    };

    const handleClear = React.useCallback(() => {
        setSelectKey(pre => pre + 1);
        setInputValue(undefined);
        setIsOpen(false);
        setErrorMessage(null);
        setLoading(false);
        setLocationData([]);

        dispatch(updateLocationNameOrCoordinate(""));
        dispatch(updateIsGetCurrentLocation(false));
        dispatch(updateSelectedLocationItem(null));
        dispatch(updateRadiusValue(10));
        dispatch(updateNeedClearLocation(false));
    }, [dispatch]);

    useEffect(() => {
        if (needClearLocation) {
            handleClear();
        }
    }, [needClearLocation, handleClear]);

    const onChangeRadius: InputNumberProps["onChange"] = (newValue) => {
        const val = Number(newValue);
        if (!isNaN(val) && Number.isInteger(val) && val >= 1 && val <= 100) {
            dispatch(updateRadiusValue(val));
        }
    };

    useEffect(() => {
        if(locationData && locationData.length > 0)return;
        else{
            dispatch(updateSelectedLocationItem(null));
            dispatch(updateLocationNameOrCoordinate(""));
        }
    },[locationData]);


    useEffect(()=>{
        dispatch(updateIsClickedSearchButton(false));
    },[locationNameOrCoordinate, selectedLocationItem]);

    console.log("WeevilTrakLocation aa render locationNameOrCoordinate: ", locationNameOrCoordinate );
    console.log("WeevilTrakLocation aa render selectedLocationItem: ", selectedLocationItem );
    console.log("WeevilTrakLocation aa render radiusValue: ", radiusValue );


    return (
        <div className="weevilTrak-location">
            <div className="weevilTrak-location-content">
                {/* Location Search */}
                <div className="weevilTrak-location-content__item">
                    <div className="weevilTrak-location-content__item__location_content">
                        <div className="location-title">Enter Location Or Coordinates (Lat,Long)</div>
                        <div>
                            <Spin spinning={loading}>
                                <div
                                    style={{display:"flex",alignItems:"center",gap:"3px"}}
                                >
                                    <AutoComplete ref={selectRef}
                                        key={selectKey}
                                        style={{ width:"600px",maxWidth:"600px",height:"40px"}}
                                        placeholder="Latitude,Longitude Or PlaceName"
                                        size="large"
                                        open={isOpen}
                                        value={inputValue || ""}
                                        onSelect={handleSelectChange}
                                        onSearch={handleSelectSearch}
                                        onBlur={handleBlur}
                                        onFocus={handleFocus}
                                    >
                                        {locationData?.map((info) => (
                                            <AutoComplete.Option key={info?.place_id} value={info?.address}>
                                                {info?.address}
                                            </AutoComplete.Option>
                                        ))}
                                    </AutoComplete>

                                </div>


                            </Spin>
                            {errorMessage && (
                                <span style={{ color: "red" }}>{errorMessage}</span>
                            )}

                        </div>
                    </div>
                    <div className="weevilTrak-location-content_stage">
                        <WeevilTrakStage />
                    </div>
                    {/* Radius */}
                    <div className="weevilTrak-location-content__item__location_radius">
                        <div className="radius-title">Radius in Km (Integer values from 1 to 100)</div>
                        <div className="radius-slider">
                            <div className="weevilTrak-slider">
                                <Slider
                                    min={1}
                                    max={100}
                                    step={1} // 设置步长为1，确保只能选择整数
                                    onChange={onChangeRadius}
                                    value={radiusValue}
                                />
                            </div>
                            <div className="weevilTrak-slider-number">
                                <InputNumber
                                    min={1}
                                    max={ 100}
                                    style={{ margin: "0 16px" }}
                                    value={radiusValue}
                                    onChange={onChangeRadius}
                                    formatter={value => `${value}`} // 格式化显示
                                    parser={value => value.replace(/[^\d]/g, "")} // 只允许输入数字字符
                                    precision={0} // 限制精度为0位小数
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/*Invoke Button  */}
                <div className="weevilTrak-location-button-container">
                    <WeevilTrakInvokeButton />
                </div>

            </div>


        </div>
    );
};

export default LightningLocation;